/* eslint-disable react/require-default-props, camelcase, @typescript-eslint/naming-convention */
import React, { Component, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { Chart, ChartSeries, ChartSeriesItem, ChartCategoryAxis, ChartCategoryAxisItem, ChartValueAxis, ChartValueAxisItem, ChartLegend, ChartTooltip } from '@progress/kendo-react-charts';
import './ChartViewStyles.scss';
const baseClass = 'qmb-ui-chart-view';
const CHART_TYPE_MAP = {
    bar: 'column',
    horizontal_bar: 'bar',
    stacked_bar: 'column',
    line: 'line',
    pie: 'pie',
    donut: 'donut'
};
class ChartErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false };
    }
    static getDerivedStateFromError() {
        return { hasError: true };
    }
    componentDidUpdate(prevProps) {
        if (this.state.hasError && prevProps.block !== this.props.block) {
            this.setState({ hasError: false });
        }
    }
    componentDidCatch(error, info) {
        // eslint-disable-next-line no-console
        console.warn('[ChartView] Kendo chart render error caught by boundary:', error.message, info.componentStack);
    }
    render() {
        if (this.state.hasError) {
            return this.props.fallback;
        }
        return this.props.children;
    }
}
/**
 * ChartView Molecule
 * Renders chart data using Kendo React Charts.
 * Supports bar, line, pie, donut, stacked_bar, and horizontal_bar.
 *
 * @version 1.1.0
 * @since 2026-02-18
 */
export function ChartView({ block, className }) {
    const [mounted, setMounted] = useState(false);
    const rafIdRef = useRef(null);
    const timeoutIdRef = useRef(null);
    useEffect(() => {
        setMounted(false);
        if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
            rafIdRef.current = window.requestAnimationFrame(() => {
                rafIdRef.current = null;
                setMounted(true);
            });
        }
        else if (typeof window !== 'undefined') {
            timeoutIdRef.current = window.setTimeout(() => {
                timeoutIdRef.current = null;
                setMounted(true);
            }, 16);
        }
        else {
            setMounted(true);
        }
        return () => {
            if (typeof window !== 'undefined' && rafIdRef.current !== null) {
                window.cancelAnimationFrame(rafIdRef.current);
                rafIdRef.current = null;
            }
            if (typeof window !== 'undefined' && timeoutIdRef.current !== null) {
                window.clearTimeout(timeoutIdRef.current);
                timeoutIdRef.current = null;
            }
        };
    }, [block]);
    const { chart_type, data, options, title } = block;
    const kendoType = CHART_TYPE_MAP[chart_type] || 'column';
    const isPie = chart_type === 'pie' || chart_type === 'donut';
    const isStacked = chart_type === 'stacked_bar' || (options === null || options === void 0 ? void 0 : options.stacked);
    const fallback = React.createElement("div", { className: `${baseClass}__empty` }, "Chart could not be rendered.");
    const hasValidDataObject = typeof data === 'object' && data !== null;
    const hasValidLabels = hasValidDataObject && Array.isArray(data.labels);
    const hasValidDatasets = hasValidDataObject &&
        Array.isArray(data.datasets) &&
        data.datasets.every((dataset) => typeof dataset === 'object' && dataset !== null && Array.isArray(dataset.data));
    const canRenderSeries = hasValidDatasets && hasValidLabels;
    // Options may arrive with short keys (x_axis/y_axis) or long keys (x_axis_label/y_axis_label)
    const xLabel = (options === null || options === void 0 ? void 0 : options.x_axis_label) || (options === null || options === void 0 ? void 0 : options.x_axis);
    const yLabel = (options === null || options === void 0 ? void 0 : options.y_axis_label) || (options === null || options === void 0 ? void 0 : options.y_axis);
    if (!canRenderSeries) {
        return (React.createElement("div", { className: classNames(baseClass, className) },
            title && React.createElement("div", { className: `${baseClass}__title` }, title),
            fallback));
    }
    return (React.createElement("div", { className: classNames(baseClass, className) },
        title && React.createElement("div", { className: `${baseClass}__title` }, title),
        React.createElement(ChartErrorBoundary, { block: block, fallback: fallback }, mounted ? (React.createElement(Chart, { transitions: false },
            (options === null || options === void 0 ? void 0 : options.show_legend) && React.createElement(ChartLegend, { position: "bottom" }),
            React.createElement(ChartTooltip, null),
            !isPie && (React.createElement(React.Fragment, null,
                React.createElement(ChartCategoryAxis, null,
                    React.createElement(ChartCategoryAxisItem, { categories: data.labels, title: xLabel ? { text: xLabel } : undefined, labels: { rotation: 'auto' } })),
                React.createElement(ChartValueAxis, null,
                    React.createElement(ChartValueAxisItem, { title: yLabel ? { text: yLabel } : undefined, majorUnit: options === null || options === void 0 ? void 0 : options.y_axis_major_unit })))),
            React.createElement(ChartSeries, null, data.datasets.map((dataset) => (React.createElement(ChartSeriesItem, { key: dataset.label, name: dataset.label, data: isPie ? data.labels.map((label, i) => ({ category: label, value: dataset.data[i] })) : dataset.data, type: kendoType, color: dataset.color, stack: isStacked ? true : undefined, field: isPie ? 'value' : undefined, categoryField: isPie ? 'category' : undefined, missingValues: kendoType === 'line' ? 'gap' : undefined })))))) : null)));
}
